import React, { useMemo, useEffect, useState, useRef, useContext } from "react";
import axios from "axios";
import FiltersGraphsCallQueue from "./FiltersGraphsCallQueue/FiltersGraphsCallQueue";
import FilteringCallQueue from "./FiltersGraphsCallQueue/FilteringCallQueue";
import TypeGraphWebexRapportCallQueue from "./Rapport/TypeGraphWebexRapportCallQueue";
import EmailPopup from "../Rapport/EmailPopup";
import { useQuery } from "@tanstack/react-query";
import { AuthContext } from "../../../../context/AuthContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import FileSaverExcel from "../../../../components/FileSaverExcel";
import { MoreVert, FileDownload } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export default function CallQueue({ data, allData, people }) {
  const { apiUrl, user } = useContext(AuthContext);
  const [filteredData, setFilteredData] = useState(data);
  const [filteredDataGroups, setFilteredDataGroups] = useState(data);
  const [linkPath, setLinkPath] = useState("Analyse par groupement");
  const [statusReport, setStatusReport] = useState(null);
  const [dataGraph, setDataGraph] = useState(null);
  const [analyseType, setAnalyseType] = useState(null);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [formattedHeader, setFormattedHeader] = useState(false);
  const [showPostPopup, setShowPostPopup] = useState(false);
  const [showGroupPopup, setShowGroupPopup] = useState(false);
  const [emailSend, setEmailSend] = useState(false);
  const [filteredPosts, setFilteredPosts] = useState({ posts: [], calltype: '' });
  const chartsRef = useRef();
  const { t } = useTranslation();

  const getcallQueue = async () => {
    try {
      const res = await axios.get(`${apiUrl}webex/calling/callQueue`, {
        withCredentials: true,
      });
      return res.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des données de la file d'attente d'appels :", error);
    }
  };

  const { data: callQueue, isLoading, isError } = useQuery({
    queryKey: ["callQueue"],
    queryFn: getcallQueue,
  });

  useEffect(() => {
    if (callQueue && !isLoading && !isError) {
      const queueNumbers = Object.values(callQueue);
      const canUseCallerId = user && user.holdingId === 33;

      const filteredCalls = filteredData.filter(call => {
         const isCalledQueue = queueNumbers.includes(call.callednumber);
         const isCallerIdQueue = canUseCallerId && call.calleridnumber && queueNumbers.includes(call.calleridnumber);
         return isCalledQueue || isCallerIdQueue;
      });
      setFilteredDataGroups(filteredCalls);
    }
  }, [data, filteredData, callQueue, isLoading, isError, user]);
  
  const groupedData = useMemo(() => {
    const callMap = new Map();
    const dataToGroup = linkPath === 'Analyse par groupement' ? filteredDataGroups : filteredData;

    dataToGroup.forEach((call) => {
      const { correlationid, starttime } = call;

      if (!callMap.has(correlationid)) {
        callMap.set(correlationid, {
          calls: [],
          starttime: new Date(starttime),
        });
      }
      callMap.get(correlationid).calls.push(call);
    });
    
    let sortedData = Array.from(callMap.values()).sort(
      (a, b) => a.starttime - b.starttime
    );

    sortedData = sortedData.map(group => {
      const filteredCalls = group.calls.filter((call, index) => {
        if (index === 0) return true;
        return call.direction === "TERMINATING";
      });
      return {
        ...group,
        calls: filteredCalls,
      };
    });
    return sortedData;
  }, [filteredData, filteredDataGroups, linkPath]);

  const createPdf = async () => {
    const element = chartsRef.current;
    const canvas = await html2canvas(element, {
      scale: 1.5,
      ignoreElements: (el) => el.classList.contains('pagination')
    });
    const imgData = canvas.toDataURL("image/jpeg", 0.75);

    const pdf = new jsPDF();
    const imgProperties = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();

    const stableWidth = 160;
    const imgHeight = (imgProperties.height * stableWidth) / imgProperties.width;

    const logoSrc = '/elitlogo.jpg';

    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
      });
    };

    try {
      const logo = await loadImage(logoSrc);
      const logoWidth = 30;
      const logoHeight = (logo.height * logoWidth) / logo.width;
      const logoX = 10;
      const logoY = 10;

      pdf.addImage(logo, 'JPEG', logoX, logoY, logoWidth, logoHeight);

      const title1 = t("ECP - Rapport Interactif");
      const title2 = t(linkPath);
      const titleMargin = 20;

      pdf.setFontSize(18);
      pdf.text(title1, pdfWidth / 2, titleMargin, { align: "center" });

      pdf.setFontSize(14);
      const title2Margin = titleMargin + 10;
      pdf.text(title2, pdfWidth / 2, title2Margin, { align: "center" });

      const positionX = (pdfWidth - stableWidth) / 2;
      const positionY = title2Margin + 10;

      pdf.addImage(imgData, "JPEG", positionX, positionY, stableWidth, imgHeight);

      return new Blob([pdf.output('blob')], { type: "application/pdf" });
    } catch (error) {
      console.error("Erreur lors du chargement de l'image du logo :", error);
    }
  };

  const sendPdf = async (pdfBlob, email) => {
    const formData = new FormData();
    formData.append("file", pdfBlob, "interface-data.pdf");
    formData.append("email", email);
    try {
      const response = await axios.post(`${apiUrl}webex/calling/mailRapport`, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setEmailSend('Succes');
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'envoi du rapport par mail :", error);
    }
  };

  const handleGenerateAndSendPdf = async (email) => {
    try {
      const pdfBlob = await createPdf();
      await sendPdf(pdfBlob, email);
    } catch (error) {
      console.error("Erreur dans le processus d'envoi du PDF :", error);
    }
  };

  const handleClickPopup = () => {
    setEmailSend(false);
  }

  return (
    <div id="callqueue">
      {emailSend && (
        <article className="page-timeline">
          <div className={`timeline-webhook-container popup-update`}>
            <button onClick={() => handleClickPopup()} className="close-alerts-status-length">
              <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="xmark" className="svg-inline--fa fa-xmark " role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                <path fill="currentColor" d="M310.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L160 210.7 54.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L114.7 256 9.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 301.3 265.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L205.3 256 310.6 150.6z"></path>
              </svg>
            </button>
            <div>{t("Email envoyé avec succès")}</div>
          </div>
        </article>
      )}
      <div className='tooltip-csv-report'>
        {statusReport === "success" && (
          <div className='tooltip-csv-tableWebex'>
            <div className="tooltip-csv-tableWebex-content-open">
              <span>{t('Exporter')}</span>
              <MoreVert />
            </div>
            <span className='tooltip-csv-tableWebex-button'>
              <div>
                <FileSaverExcel data={[dataGraph]} name={`${linkPath}`} formattedHeader={formattedHeader}/>
                <span className="saveCSVWebexCalling" onClick={() => setShowEmailPopup(true) + setShowPostPopup(false)}><FileDownload />{t('Exporter en PDF et envoyer par email')}</span>
              </div>
            </span>
          </div>
        )}
      </div>
      <div className="statistics-report statistics-report-callqueue">
        <FiltersGraphsCallQueue linkPath={linkPath} setLinkPath={setLinkPath} />
        {linkPath !== null && callQueue && filteredDataGroups && (
          <FilteringCallQueue 
            data={data} 
            people={people} 
            setFilteredData={setFilteredData} 
            reportType={linkPath} 
            setStatusReport={setStatusReport} 
            setAnalyseType={setAnalyseType} 
            showPostPopup={showPostPopup} 
            setShowPostPopup={setShowPostPopup} 
            showGroupPopup={showGroupPopup} 
            setShowGroupPopup={setShowGroupPopup} 
            setShowEmailPopup={setShowEmailPopup} 
            callQueue={callQueue} 
            user={user} 
            setFilteredPosts={setFilteredPosts}
          />
        )}
      </div>
      {showEmailPopup && (
        <EmailPopup
          onSubmit={(email) => {
            setShowEmailPopup(false);
            handleGenerateAndSendPdf(email);
          }}
          onCancel={() => setShowEmailPopup(false)}
        />
      )}
      <div className="rapport-pdf">
        {statusReport === "success" && (
          <>
            <div ref={chartsRef} className="container-statistics-webex-report">
              <TypeGraphWebexRapportCallQueue 
                data={groupedData} 
                allCalls={allData} 
                flatCalls={data}
                callQueue={callQueue} 
                people={people} 
                reportType={linkPath} 
                analyseType={analyseType} 
                setDataGraph={setDataGraph} 
                setFormattedHeader={setFormattedHeader} 
                user={user}
                filteredPosts={filteredPosts}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
